import { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import useFilterManagerWithState from '@/components/FilterManager/useFilterManagerWithState';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import useAsyncListActions from '@/hooks/useList/useAsyncListActions';
import {
  LearnerPageFilters,
  LearnerStatusFilters,
} from '@/modules/corporate/modules/learners/views/LearnerManager/types';
import {
  getLearnersSortDefaultValue,
  getSortAndFilterItems,
} from '@/modules/corporate/modules/learners/views/LearnerManager/utils';
import { UserTypeEnum } from '@/types/schema';

import { DialogDataConfig } from '../types';

type UseLearnerProps = {
  pause?: boolean;
  selectedLearnerIds: string[];
  filterManager: ReturnType<typeof useFilterManagerWithState>;
  isInGroupCheckId?: string;
  userGroupIds?: string[];
  removedUserIds?: string[];
};

export default function useLearner(props: UseLearnerProps): DialogDataConfig {
  const {
    pause,
    selectedLearnerIds,
    filterManager,
    isInGroupCheckId,
    userGroupIds = [],
    removedUserIds,
  } = props;
  const { t } = useTranslation();
  const asyncList = useAsyncListActions({
    includeIds: selectedLearnerIds,
    excludeIds: removedUserIds ?? [],
    isSelectAll: false,
    userGroupIds,
  });
  const { methods } = asyncList;
  const { getSelectedCount } = methods;
  const {
    state: { sort, filter, search },
  } = filterManager;
  const userGroupIdsOfState = [...(asyncList.state.userGroupIds ?? [])];
  const sortAndFilterItems = useMemo(
    () => getSortAndFilterItems(t, LearnerPageFilters.DEFAULT, LearnerStatusFilters.ACTIVE),
    [t],
  );

  const { isFetching, data, fetchMore, error, totalCount } = useUsers({
    ...filter,
    sort: sort?.length ? sort : getLearnersSortDefaultValue(LearnerStatusFilters.ACTIVE),
    searchText: search,
    userTypes: [UserTypeEnum.Learner],
    pause,
    isInGroupCheckId,
    userGroupIds: userGroupIdsOfState,
  });

  const selectedCount = getSelectedCount(totalCount);

  return {
    isFetching,
    data,
    error,
    fetchMore,
    selectedCount,
    asyncList,
    sortAndFilterItems,
  };
}
